(function () {
  'use strict';

  class WallpaperFinderModal {
    constructor(element) {
      this.element = element;
      this.dialog = element.querySelector('.wallpaper-finder-modal__dialog');
      this.endpoint = element.dataset.endpoint;
      this.turnstileSiteKey = String(element.dataset.turnstileSitekey || '').trim();
      this.turnstileContainer = element.querySelector('[data-wallpaper-finder-turnstile]');
      this.turnstileWidgetId = null;
      this.modeTabs = [...element.querySelectorAll('[data-wallpaper-finder-mode]')];
      this.panels = [...element.querySelectorAll('[data-wallpaper-finder-panel]')];
      this.textForm = element.querySelector('[data-wallpaper-finder-text-form]');
      this.query = element.querySelector('[data-wallpaper-finder-query]');
      this.textSubmit = element.querySelector('[data-wallpaper-finder-text-submit]');
      this.roomForm = element.querySelector('[data-wallpaper-finder-room-form]');
      this.roomQuery = element.querySelector('[data-wallpaper-finder-room-query]');
      this.roomInput = element.querySelector('[data-wallpaper-finder-room-input]');
      this.roomDrop = element.querySelector('[data-wallpaper-finder-room-drop]');
      this.roomPreview = element.querySelector('[data-wallpaper-finder-room-preview]');
      this.roomImage = element.querySelector('[data-wallpaper-finder-room-image]');
      this.roomName = element.querySelector('[data-wallpaper-finder-room-name]');
      this.roomSubmit = element.querySelector('[data-wallpaper-finder-room-submit]');
      this.status = element.querySelector('[data-wallpaper-finder-status]');
      this.brief = element.querySelector('[data-wallpaper-finder-brief]');
      this.summary = element.querySelector('[data-wallpaper-finder-summary]');
      this.chips = element.querySelector('[data-wallpaper-finder-chips]');
      this.results = element.querySelector('[data-wallpaper-finder-results]');
      this.resultsTitle = element.querySelector('[data-wallpaper-finder-results-title]');
      this.announcement = element.querySelector('[data-wallpaper-finder-announcement]');
      this.grid = element.querySelector('[data-wallpaper-finder-grid]');
      this.count = element.querySelector('[data-wallpaper-finder-count]');
      this.productResults = new window.MurallsVisualProductResults({
        grid: this.grid,
        count: this.count,
        source: 'wallpaper_finder',
        idPrefix: 'WallpaperFinder'
      });
      this.activeMode = 'text';
      this.lastFocus = null;
      this.roomFile = null;
      this.roomPreviewUrl = '';
      this.requestId = 0;
      this.abortController = null;
      this.closeTimer = null;
      this.bind();
    }

    bind() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-wallpaper-finder-open]');
        if (button) this.open(button);
      });
      this.element.querySelectorAll('[data-wallpaper-finder-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.modeTabs.forEach((tab) => {
        tab.addEventListener('click', () => this.setMode(tab.dataset.wallpaperFinderMode));
        tab.addEventListener('keydown', (event) => this.onModeKeydown(event, tab));
      });
      this.element.querySelectorAll('[data-wallpaper-finder-example]').forEach((button) => {
        button.addEventListener('click', () => {
          this.query.value = button.dataset.wallpaperFinderExample || '';
          this.query.focus();
        });
      });
      this.textForm.addEventListener('submit', (event) => this.submitText(event));
      this.roomForm.addEventListener('submit', (event) => this.submitRoom(event));
      this.roomInput.addEventListener('change', () => this.setRoomFile(this.roomInput.files[0]));
      this.element.querySelector('[data-wallpaper-finder-room-change]').addEventListener('click', () => this.roomInput.click());
      ['dragenter', 'dragover'].forEach((name) => this.roomDrop.addEventListener(name, (event) => {
        event.preventDefault();
        this.roomDrop.classList.add('is-dragging');
      }));
      ['dragleave', 'drop'].forEach((name) => this.roomDrop.addEventListener(name, (event) => {
        event.preventDefault();
        this.roomDrop.classList.remove('is-dragging');
      }));
      this.roomDrop.addEventListener('drop', (event) => this.setRoomFile(event.dataTransfer.files[0]));
      this.element.querySelector('[data-wallpaper-finder-visual-search]').addEventListener('click', () => this.openVisualSearch());
      ['click', 'keydown'].forEach((eventName) => {
        this.grid.addEventListener(eventName, (event) => {
          if (!event.target.closest('[data-visual-product-tab]')) return;
          if (event.type === 'keydown' && !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          requestAnimationFrame(() => {
            this.syncResultTabStops();
            this.refreshFocusTrap(document.activeElement);
          });
        });
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.element.hidden) this.close();
      });
    }

    open(button) {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      const parentModal = button?.closest('details-modal');
      this.lastFocus = parentModal?.querySelector('summary') || button || document.activeElement;
      if (parentModal?.isOpen?.()) parentModal.close(false);
      this.element.hidden = false;
      this.dialog.scrollTop = 0;
      document.body.classList.add('wallpaper-finder-open');
      requestAnimationFrame(() => this.element.classList.add('is-open'));
      this.refreshFocusTrap(this.initialFocusForMode());
    }

    close(restoreFocus = true) {
      if (this.element.hidden) return;
      const wasSearching = Boolean(this.abortController);
      this.abortActiveRequest();
      if (wasSearching) this.clearDiscovery();
      this.element.classList.remove('is-open');
      document.body.classList.remove('wallpaper-finder-open');
      const focusTarget = restoreFocus && this.lastFocus?.isConnected ? this.lastFocus : null;
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(focusTarget);
      else focusTarget?.focus();
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.element.hidden = true;
        this.closeTimer = null;
      }, 180);
    }

    setMode(mode) {
      if (!['text', 'room'].includes(mode)) return;
      const changed = mode !== this.activeMode;
      if (changed) {
        this.abortActiveRequest();
        this.clearDiscovery();
      }
      this.activeMode = mode;
      this.modeTabs.forEach((tab) => {
        const active = tab.dataset.wallpaperFinderMode === mode;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.wallpaperFinderPanel !== mode; });
      this.refreshFocusTrap(this.initialFocusForMode());
    }

    onModeKeydown(event, tab) {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const next = this.modeTabs[(this.modeTabs.indexOf(tab) + direction + this.modeTabs.length) % this.modeTabs.length];
      this.setMode(next.dataset.wallpaperFinderMode);
      next.focus();
    }

    async submitText(event) {
      event.preventDefault();
      const query = this.query.value.trim();
      if (query.length < 3) {
        this.abortActiveRequest();
        this.showError('Please describe the wallpaper you have in mind.');
        this.query.focus();
        return;
      }
      const request = this.beginRequest('text');
      this.startSearch('Understanding your style…', 'AI is matching your request with our collection.');
      window.MurallsFunnel?.trackSearch('wallpaper_finder', { mode: 'text' });
      try {
        const challenge = await this.getTurnstileToken('finder_text');
        if (request.controller.signal.aborted || request.id !== this.requestId) return;
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mode: 'text',
            query,
            turnstile_token: challenge.token,
            turnstile_action: challenge.action
          }),
          signal: request.controller.signal
        });
        const data = await this.readResponse(response);
        if (!this.finishRequest(request.id)) return;
        this.renderResponse(data);
      } catch (error) {
        if (error?.name === 'AbortError' || request.id !== this.requestId) return;
        this.finishRequest(request.id);
        console.error('Wallpaper finder text search error:', error);
        this.showError(this.errorMessage(error));
      }
    }

    async submitRoom(event) {
      event.preventDefault();
      if (!this.roomFile) {
        this.abortActiveRequest();
        this.showError('Please upload a room photo first.');
        this.roomInput.focus();
        return;
      }
      const request = this.beginRequest('room');
      this.startSearch('Reading your room…', 'AI is considering the colors, light and furniture.');
      window.MurallsFunnel?.trackSearch('wallpaper_finder', { mode: 'room' });
      try {
        const [prepared, challenge] = await Promise.all([
          this.prepareImage(this.roomFile),
          this.getTurnstileToken('finder_room')
        ]);
        if (request.controller.signal.aborted || request.id !== this.requestId) return;
        const form = new FormData();
        form.append('mode', 'room');
        form.append('image', prepared.blob, 'room.jpg');
        form.append('intent', this.roomForm.elements.intent.value || 'match');
        form.append('turnstile_token', challenge.token);
        form.append('turnstile_action', challenge.action);
        const query = this.roomQuery.value.trim();
        if (query) form.append('query', query);
        const response = await fetch(this.endpoint, { method: 'POST', body: form, signal: request.controller.signal });
        const data = await this.readResponse(response);
        if (!this.finishRequest(request.id)) return;
        this.renderResponse(data);
      } catch (error) {
        if (error?.name === 'AbortError' || request.id !== this.requestId) return;
        this.finishRequest(request.id);
        console.error('Wallpaper finder room search error:', error);
        this.showError(this.errorMessage(error));
      }
    }

    async readResponse(response) {
      let data = {};
      try { data = await response.json(); } catch {}
      if (!response.ok) {
        const error = new Error(data.error || 'search_failed');
        error.status = response.status;
        throw error;
      }
      return data;
    }

    setRoomFile(file) {
      if (!file) return;
      this.abortActiveRequest();
      this.clearDiscovery();
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        this.showError('Please choose a JPEG, PNG or WebP image.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.showError('The room photo is too large. Please choose one under 5 MB.');
        return;
      }
      this.roomFile = file;
      if (this.roomPreviewUrl) URL.revokeObjectURL(this.roomPreviewUrl);
      this.roomPreviewUrl = URL.createObjectURL(file);
      this.roomImage.src = this.roomPreviewUrl;
      this.roomName.textContent = file.name;
      this.roomDrop.hidden = true;
      this.roomPreview.hidden = false;
      this.setSubmitting(false);
      window.MurallsFunnel?.trackUpload('wallpaper_finder', { mode: 'room' });
    }

    prepareImage(file) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        const url = URL.createObjectURL(file);
        image.onload = () => {
          const scale = Math.min(1, 1200 / Math.max(image.naturalWidth, image.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
          canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          canvas.toBlob((blob) => blob ? resolve({ blob }) : reject(new Error('image_conversion_failed')), 'image/jpeg', 0.88);
        };
        image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image_load_failed')); };
        image.src = url;
      });
    }

    async getTurnstileToken(action) {
      if (!this.turnstileSiteKey) return { token: '', action: '' };
      await loadTurnstileScript();
      if (!window.turnstile || !this.turnstileContainer) throw new Error('verification_unavailable');
      if (this.turnstileWidgetId !== null) {
        try { window.turnstile.remove(this.turnstileWidgetId); } catch {}
        this.turnstileWidgetId = null;
      }
      return new Promise((resolve, reject) => {
        let settled = false;
        const timeout = window.setTimeout(() => finish(new Error('verification_timeout')), 15000);
        const finish = (error, token) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          if (error) reject(error);
          else resolve({ token, action });
        };
        try {
          this.turnstileWidgetId = window.turnstile.render(this.turnstileContainer, {
            sitekey: this.turnstileSiteKey,
            action,
            appearance: 'interaction-only',
            execution: 'execute',
            callback: (token) => finish(null, token),
            'error-callback': () => finish(new Error('verification_failed')),
            'expired-callback': () => finish(new Error('verification_expired'))
          });
          window.turnstile.execute(this.turnstileWidgetId);
        } catch (error) {
          finish(error);
        }
      });
    }

    startSearch(title, copy) {
      this.clearDiscovery();
      this.dialog.setAttribute('aria-busy', 'true');
      this.status.classList.remove('is-error');
      this.status.replaceChildren();
      const spinner = document.createElement('span');
      spinner.className = 'visual-search-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      const strong = document.createElement('strong');
      strong.textContent = title;
      const small = document.createElement('small');
      small.textContent = copy;
      this.status.append(spinner, strong, small);
      this.status.hidden = false;
    }

    renderResponse(data) {
      this.dialog.removeAttribute('aria-busy');
      const products = Array.isArray(data.products) ? data.products : [];
      const rendered = this.productResults.render(products, data.groups, data.preferredType);
      this.status.hidden = true;
      if (!rendered) {
        this.showError('We couldn’t find a close match. Try a broader description or another room photo.');
        return;
      }
      this.renderBrief(data);
      this.syncResultTabStops();
      this.results.hidden = false;
      this.announce(`${rendered} ${rendered === 1 ? 'design' : 'designs'} found. Closest matches are ready.`);
      this.refreshFocusTrap(this.resultsTitle);
      this.results.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }

    renderBrief(data) {
      const summary = String(data.summary || '').trim();
      const understood = data.understood && typeof data.understood === 'object' ? data.understood : {};
      const labels = {
        room: 'Room', color: 'Color', style: 'Style', theme: 'Theme', scale: 'Scale', tone: 'Tone',
        density: 'Density', light: 'Light', animal: 'Animal', kids: 'Kids', floral: 'Floral',
        nature: 'Nature', city: 'City'
      };
      const entries = [];
      const filters = understood.filters && !Array.isArray(understood.filters) && typeof understood.filters === 'object'
        ? understood.filters
        : {};
      ['room', 'color', 'style', 'theme', 'floral', 'nature', 'animal', 'kids', 'scale', 'density', 'tone', 'light', 'city'].forEach((key) => {
        const values = Array.isArray(filters[key]) ? filters[key] : [];
        values.slice(0, 2).forEach((item) => {
          const text = typeof item === 'string' ? this.titleCase(item) : '';
          if (text) entries.push(`${labels[key]}: ${text}`);
        });
      });
      const excludeTerms = Array.isArray(understood.excludeTerms)
        ? understood.excludeTerms
        : (Array.isArray(filters.excludeTerms)
            ? filters.excludeTerms
            : (Array.isArray(data.excludeTerms) ? data.excludeTerms : []));
      const avoidEntries = [];
      excludeTerms.filter((item) => typeof item === 'string' && item.trim()).slice(0, 2).forEach((item) => {
        const text = typeof item === 'string' ? this.titleCase(item) : '';
        if (text) avoidEntries.push(`Avoid: ${text}`);
      });
      this.summary.textContent = summary || 'We matched the strongest details in your request.';
      this.chips.replaceChildren();
      [...entries.slice(0, 5), ...avoidEntries].forEach((entry) => {
        const chip = document.createElement('span');
        chip.textContent = entry;
        this.chips.appendChild(chip);
      });
      this.brief.hidden = false;
    }

    titleCase(value) {
      return String(value || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    showError(message) {
      this.clearDiscovery({ clearStatus: false });
      this.dialog.removeAttribute('aria-busy');
      this.setSubmitting(false);
      this.status.replaceChildren();
      this.status.classList.add('is-error');
      this.status.textContent = message;
      this.status.hidden = false;
    }

    clearDiscovery({ clearStatus = true } = {}) {
      this.results.hidden = true;
      this.brief.hidden = true;
      this.productResults.clear();
      this.summary.textContent = '';
      this.chips.replaceChildren();
      if (this.announcement) this.announcement.textContent = '';
      if (!clearStatus) return;
      this.status.hidden = true;
      this.status.classList.remove('is-error');
      this.status.replaceChildren();
    }

    beginRequest(mode) {
      this.abortActiveRequest();
      const controller = new AbortController();
      const id = ++this.requestId;
      this.abortController = controller;
      this.setSubmitting(mode);
      return { controller, id };
    }

    finishRequest(id) {
      if (id !== this.requestId) return false;
      this.abortController = null;
      this.setSubmitting(false);
      return true;
    }

    abortActiveRequest() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
      this.requestId += 1;
      this.dialog.removeAttribute('aria-busy');
      this.setSubmitting(false);
    }

    setSubmitting(mode) {
      const busy = Boolean(mode);
      if (this.textSubmit) this.textSubmit.disabled = busy;
      this.roomSubmit.disabled = busy || !this.roomFile;
    }

    initialFocusForMode() {
      if (this.activeMode !== 'room') return this.query;
      return this.roomFile ? this.roomQuery : this.roomInput;
    }

    refreshFocusTrap(focusTarget) {
      const target = focusTarget?.isConnected && !focusTarget.closest('[hidden]') ? focusTarget : this.initialFocusForMode();
      if (typeof window.trapFocus === 'function') window.trapFocus(this.dialog, target);
      else target?.focus();
    }

    syncResultTabStops() {
      this.grid.querySelectorAll('[data-visual-product-panel]').forEach((panel) => {
        panel.querySelectorAll('a[href]').forEach((link) => {
          if (panel.hidden) link.tabIndex = -1;
          else link.removeAttribute('tabindex');
        });
      });
    }

    announce(message) {
      if (!this.announcement) return;
      this.announcement.textContent = '';
      requestAnimationFrame(() => { this.announcement.textContent = message; });
    }

    errorMessage(error) {
      if (error?.status === 429) return 'Too many searches right now. Please wait a moment and try again.';
      if (error?.status === 403 || String(error?.message || '').startsWith('verification_')) {
        return 'We couldn’t verify this search. Please refresh the page and try again.';
      }
      if (error?.status === 503) return 'Search is temporarily busy. Please try again a little later.';
      return 'We couldn’t complete this search. Please try again.';
    }

    openVisualSearch() {
      const returnFocus = this.lastFocus?.isConnected ? this.lastFocus : document.body;
      this.close(false);
      window.setTimeout(() => {
        const attribution = window.MurallsFunnel?.activate('visual_search');
        window.MurallsFunnel?.send('visual_search', 'opened', { sessionId: attribution?.sessionId });
        if (window.MurallsTools) window.MurallsTools.open('visual', returnFocus);
        else window.murallsVisualSearch?.open(returnFocus);
      }, 190);
    }
  }

  let turnstileScriptPromise;
  function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (turnstileScriptPromise) return turnstileScriptPromise;
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-wallpaper-finder-turnstile-script]');
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', () => reject(new Error('verification_unavailable')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.wallpaperFinderTurnstileScript = '';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => reject(new Error('verification_unavailable')), { once: true });
      document.head.appendChild(script);
    });
    return turnstileScriptPromise;
  }

  const initialize = () => {
    const element = document.querySelector('[data-wallpaper-finder]');
    if (element && !window.murallsWallpaperFinder) window.murallsWallpaperFinder = new WallpaperFinderModal(element);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
